import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import { useUser } from '~/modules/auth/hooks/useUser';
import { Breadcrumbs } from '~/modules/navigation/components/Breadcrumbs';

import { getItemData, getTrackVersions } from '../../api/dashboard';
import type { TrackIgnore } from '../../api/track-ignore';
import {
  getTrackIgnore,
  ignoreTrack,
  unignoreTrack,
} from '../../api/track-ignore';
import type { TrackLock } from '../../api/track-lock';
import { getTrackLock, lockTrack, unlockTrack } from '../../api/track-lock';
import type { DashboardItem, TrackVersion } from '../../types';
import { ErrorDisplay } from '../LabelPerf/components';
import { GeneralInfo } from './components/GeneralInfo';
import { VersionsInfo } from './components/Versions';

type TabCode = 'general' | 'versions';

export function TrackDetails() {
  const { t } = useTranslation('label-perf');
  const { trackId } = useParams<{ trackId: string }>();
  const [data, setData] = useState<DashboardItem>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [activeTab, setActiveTab] = useState<TabCode>('general');
  const [versions, setVersions] = useState<TrackVersion[]>([]);
  const [trackLock, setTrackLock] = useState<TrackLock | null>(null);
  const [trackIgnore, setTrackIgnore] = useState<TrackIgnore | null>(null);
  const user = useUser();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const id = trackId!;

      try {
        setLoading(true);

        const results = await Promise.allSettled([
          getItemData(id),
          getTrackVersions(id),
          getTrackLock(id),
          getTrackIgnore(id),
        ]);

        const [
          itemDataResult,
          trackVersionsResult,
          trackLockResult,
          trackIgnoreResult,
        ] = results;

        // Check if getItemData was successful
        if (itemDataResult.status === 'fulfilled') {
          setData(itemDataResult.value);
        } else {
          setError('Failed to fetch item data');
        }

        // Check if getTrackVersions was successful
        if (trackVersionsResult.status === 'fulfilled') {
          setVersions(trackVersionsResult.value);
        } else {
          setError('Failed to fetch track versions');
        }

        // Handle getActiveTrackLock
        if (trackLockResult.status === 'fulfilled' && trackLockResult.value) {
          setTrackLock(trackLockResult.value);
        }

        // Handle getActiveTrackLock
        if (
          trackIgnoreResult.status === 'fulfilled' &&
          trackIgnoreResult.value
        ) {
          setTrackIgnore(trackIgnoreResult.value);
        }
      } catch {
        setError('An error occurred while fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [trackId]);

  const handleTrackLock = async (): Promise<void> => {
    try {
      const result = !trackLock
        ? await lockTrack(trackId!)
        : await unlockTrack(trackId!);

      setTrackLock(!trackLock ? result : null); // Update track lock state
    } catch (_error) {
      setError((_error as Error).message);

      console.error('Error creating track lock:', error);
    }
  };

  const handleTrackIgnore = async (): Promise<void> => {
    try {
      const result = !trackIgnore
        ? await ignoreTrack(trackId!)
        : await unignoreTrack(trackId!);

      setTrackIgnore(!trackIgnore ? result : null); // Update track lock state
    } catch (_error) {
      setError((_error as Error).message);

      console.error('Error creating track lock:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-xl">
        <Icon type="spinner" variant="solid" size="md" className="fa-spin" />
      </div>
    );
  }

  if (!data || error || !trackId) {
    return (
      <ErrorDisplay
        description={t('body.trackNotFoundDesc')}
        className="m-xl p-xl"
        title={t('noData.trackNotFound')}
      />
    );
  }

  return (
    <div>
      <Helmet>
        <title>{data.isrc}</title>
      </Helmet>

      <div className="p-xl">
        <Breadcrumbs.Root className="mb-lg flex w-full items-center justify-start gap-sm text-body-xs">
          <Breadcrumbs.Item
            to="/catalog"
            className="font-semibold underline transition text-hover hover:text-white"
          >
            {t('title.catalog')}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item>
            {t('body.isrc')}: {data.isrc}
          </Breadcrumbs.Item>
        </Breadcrumbs.Root>

        {versions.length > 1 && (
          <div className="mb-md flex border-b border-emphasis-subtle">
            <button
              className={cn('py-sm text-body-md font-bold', {
                'border-b-[0.2em] border-tertiary-default':
                  activeTab === 'general',
              })}
              onClick={() => setActiveTab('general')}
            >
              {t('body.general')}
            </button>
            <button
              className={cn('ml-md py-sm text-body-md font-bold', {
                'border-b-[0.2em] border-tertiary-default':
                  activeTab === 'versions',
              })}
              onClick={() => setActiveTab('versions')}
            >
              {t('body.versions')}{' '}
              <span className="text-body-xs font-normal text-default">
                ({versions.length})
              </span>
            </button>
          </div>
        )}

        {activeTab === 'general' && (
          <GeneralInfo
            currentUser={user}
            data={data}
            handleTrackIgnore={handleTrackIgnore}
            handleTrackLock={handleTrackLock}
            isrc={trackId}
            trackIgnore={trackIgnore}
            trackLock={trackLock}
          />
        )}
        {activeTab === 'versions' && <VersionsInfo versions={versions} />}
      </div>
    </div>
  );
}

TrackDetails.displayName = 'TrackDetails';
